/**
 * "Instalar plugin…" (docs/spec/web-plugins.md, "Como se usa"): um diálogo em dois passos. Primeiro as
 * origens, em abas: os plugins oficiais do catálogo (os do sistema da mesa no alto, instalar direto
 * da lista), o Desk Cloud de quem instala e o computador. Um arquivo escolhido vai para a confirmação:
 * nome, versão, autor, fichas e tabelas, webhooks e o relatório do verificador (com "Copiar
 * relatório", para levar a uma IA), e "Voltar" para as origens.
 */
import { useState } from 'react';
import { useLanguage, useT } from '../../i18n/index.js';
import type { OfficialPlugin } from '../../shared.js';
import { describeError, useApp } from '../../store.js';
import { Button, Dialog, Spinner } from '../../ui/controls.js';
import { EmptyState } from '../../ui/EmptyState.js';
import { Icon } from '../../ui/icons.js';
import { askConfirm } from '../../ui/prompt.js';
import { toast } from '../../ui/Toaster.js';
import { catalogGroups, catalogState, useOfficialPlugins, type CatalogEntryState } from './officialCatalog.js';
import { CloudPluginPanel, ComputerPluginPanel, DialogTabs, PluginIcon, PluginSummary, inspectionReport, pluginMeta } from './PluginParts.js';
import { compareVersions, localize } from './sdk.js';
import { readCloudPlugin, readPluginFile, sendWebInstall, useWebInstall, type InstallTab } from './install.js';

const TAB_IDS = 'rd-plugin-install';

export function WebInstallDialog() {
  const t = useT();
  const state = useWebInstall();
  const [reading, setReading] = useState(false);
  if (!state.open) return null;
  const { tab, pending, busy, cloudChoice } = state;

  const close = () => {
    if (!busy) state.close();
  };
  /** Lê e confere o arquivo escolhido; o erro de leitura fica num aviso, nas origens. */
  const read = async (task: () => Promise<void>) => {
    setReading(true);
    try {
      await task();
    } catch (error) {
      toast(t('library.pluginFailed', { message: describeError(error) }), 'error');
    } finally {
      setReading(false);
    }
  };

  const tabs: { key: InstallTab; label: string }[] = [
    { key: 'official', label: t('pluginInstall.tab.official') },
    { key: 'cloud', label: t('pluginInstall.tab.cloud') },
    { key: 'computer', label: t('pluginInstall.tab.computer') },
  ];

  const sourceActions =
    tab === 'cloud' ? (
      <>
        <Button variant="secondary" onClick={close}>
          {t('create.cancel')}
        </Button>
        <Button variant="action" busy={reading} disabled={!cloudChoice} onClick={() => cloudChoice && void read(() => readCloudPlugin(cloudChoice))}>
          {t('pluginInstall.cloud.continue')}
        </Button>
      </>
    ) : (
      <Button variant="secondary" onClick={close}>
        {tab === 'official' ? t('common.close') : t('create.cancel')}
      </Button>
    );

  return (
    <Dialog title={t('webInstall.title')} onClose={close} wide className="rd-pluginInstall" actions={pending ? <ConfirmActions /> : sourceActions}>
      {pending ? <ConfirmBody /> : null}
      {/* as origens ficam montadas na confirmação: "Voltar" acha a pasta e a busca como estavam */}
      <div className="rd-pluginInstall__sources" hidden={!!pending}>
        <DialogTabs tabs={tabs} value={tab} onChange={state.setTab} label={t('pluginInstall.tabs')} idBase={TAB_IDS} />
        <div className="rd-pluginInstall__panel" role="tabpanel" id={`${TAB_IDS}-${tab}`} aria-labelledby={`${TAB_IDS}-tab-${tab}`}>
          {tab === 'official' ? <OfficialPanel onSwitch={state.setTab} /> : null}
          {tab === 'cloud' ? (
            <CloudPluginPanel chosen={cloudChoice} onChoose={state.chooseCloud} onPick={(file) => void read(() => readCloudPlugin(file))} hint={t('pluginInstall.cloud.hint')} />
          ) : null}
          {tab === 'computer' ? <ComputerPluginPanel busy={reading} onFile={(file) => void read(() => readPluginFile(file))} hint={t('pluginInstall.computer.hint')} /> : null}
        </div>
      </div>
    </Dialog>
  );
}

/** O que a mesa tem do plugin da confirmação (o mesmo `id` de manifesto), para "1.2.0 → 1.3.0". */
function useInstalledVersion(): string | null {
  const pending = useWebInstall((s) => s.pending);
  const plugins = useApp((s) => s.plugins);
  const id = pending?.inspection.manifest?.id;
  return (id && plugins.find((p) => p.moduleId === id)?.version) || null;
}

function ConfirmBody() {
  const t = useT();
  const pending = useWebInstall((s) => s.pending)!;
  const installed = useInstalledVersion();
  const m = pending.inspection.manifest;
  const notice =
    m && installed ? (
      <p className={compareVersions(m.version, installed) < 0 ? 'rd-webinstall__warn' : 'rd-webinstall__hint'}>
        {t(compareVersions(m.version, installed) < 0 ? 'webInstall.downgrade' : 'webInstall.updating', { from: installed, to: m.version })}
      </p>
    ) : null;
  return <PluginSummary inspection={pending.inspection} fileName={pending.fileName} notice={notice} />;
}

function ConfirmActions() {
  const t = useT();
  const language = useLanguage((s) => s.language);
  const { pending, busy, fromSources, back, close, setBusy } = useWebInstall();
  const installed = useInstalledVersion();
  if (!pending) return null;
  const { inspection, fileName } = pending;
  const errors = inspection.issues.filter((i) => i.level === 'error');
  const name = inspection.manifest ? localize(inspection.manifest.name, language) : fileName;

  const copy = () => void navigator.clipboard.writeText(inspectionReport(inspection, fileName, language)).then(() => toast(t('webInstall.copied'), 'success'));
  const install = async () => {
    setBusy(true);
    try {
      const error = await sendWebInstall(pending);
      if (error) {
        setBusy(false);
        toast(t('library.pluginFailed', { message: error }), 'error');
        return;
      }
      close();
      toast(t('library.pluginInstalled', { name }), 'success');
    } catch (error) {
      setBusy(false);
      toast(t('library.pluginFailed', { message: describeError(error) }), 'error');
    }
  };

  return (
    <>
      {fromSources ? (
        <Button variant="ghost" className="rd-pluginInstall__back" icon="chevronLeft" disabled={busy} onClick={back}>
          {t('common.back')}
        </Button>
      ) : null}
      {inspection.issues.length ? (
        <Button variant="secondary" onClick={copy}>
          {t('webInstall.copy')}
        </Button>
      ) : null}
      <Button variant="secondary" disabled={busy} onClick={close}>
        {t('create.cancel')}
      </Button>
      <Button variant="action" busy={busy} disabled={errors.length > 0 || !inspection.pkg} onClick={() => void install()}>
        {busy ? t('webInstall.installing') : installed ? t('webInstall.update') : t('webInstall.install')}
      </Button>
    </>
  );
}

/**
 * A aba Oficiais: o catálogo, em dois grupos quando a mesa tem sistema e algum oficial serve para ele
 * ("Para D&D 5e" e "Outros"). Instalar e atualizar acontecem na própria linha; atualizar pede
 * confirmação, porque as fichas abertas recarregam.
 */
function OfficialPanel({ onSwitch }: { onSwitch: (tab: InstallTab) => void }) {
  const t = useT();
  const language = useLanguage((s) => s.language);
  const plugins = useApp((s) => s.plugins);
  const roomSystem = useApp((s) => s.room?.rpgSystem ?? '');
  const { plugins: entries, error, retry } = useOfficialPlugins();
  const [busyId, setBusyId] = useState<string | null>(null);

  if (entries === null) {
    return error ? (
      <div className="rd-pluginCatalog__state" role="alert">
        <p>{t('pluginInstall.official.failed', { message: error })}</p>
        <Button variant="secondary" onClick={retry}>
          {t('pluginInstall.retry')}
        </Button>
      </div>
    ) : (
      <div className="rd-pluginCatalog__state">
        <Spinner size={22} />
      </div>
    );
  }
  if (!entries.length) {
    return (
      <EmptyState icon="sheet" className="rd-pluginCatalog__empty" title={t('pluginInstall.official.none')} text={t('pluginInstall.official.noneText')}>
        <Button variant="secondary" onClick={() => onSwitch('cloud')}>
          {t('pluginInstall.tab.cloud')}
        </Button>
        <Button variant="secondary" onClick={() => onSwitch('computer')}>
          {t('pluginInstall.tab.computer')}
        </Button>
      </EmptyState>
    );
  }

  const install = async (entry: OfficialPlugin, state: CatalogEntryState) => {
    const name = localize(entry.name, language);
    if (state.kind === 'update') {
      const sure = await askConfirm({
        title: t('pluginInstall.updateTitle', { name, version: entry.version }),
        message: t('webInstall.updating', { from: state.from, to: entry.version }),
        action: t('webInstall.update'),
      });
      if (!sure) return;
    }
    setBusyId(entry.id);
    const result = await useApp.getState().installOfficialPlugin(entry.id);
    setBusyId(null);
    if (result.ok) toast(t(state.kind === 'update' ? 'library.pluginUpdated' : 'library.pluginInstalled', { name, version: entry.version }), 'success');
    else toast(t('library.pluginFailed', { message: result.error ?? t('webInstall.failed') }), 'error');
  };

  const { forSystem, others } = catalogGroups(entries, roomSystem, language);
  const row = (entry: OfficialPlugin, likely: boolean) => (
    <OfficialRow key={entry.id} entry={entry} state={catalogState(entry, plugins)} likely={likely} busy={busyId === entry.id} disabled={busyId !== null} onInstall={install} />
  );
  return (
    <div className="rd-pluginCatalog">
      {forSystem.length ? (
        <section className="rd-pluginCatalog__group" aria-labelledby="rd-plugin-catalog-system">
          <h3 className="rd-cloudSection__title" id="rd-plugin-catalog-system">
            {t('pluginInstall.official.forSystem', { system: roomSystem.trim() })}
          </h3>
          <ul className="rd-pluginCatalog__list">{forSystem.map((entry) => row(entry, true))}</ul>
        </section>
      ) : null}
      {others.length ? (
        <section className="rd-pluginCatalog__group" aria-labelledby={forSystem.length ? 'rd-plugin-catalog-others' : undefined} aria-label={forSystem.length ? undefined : t('pluginInstall.tab.official')}>
          {forSystem.length ? (
            <h3 className="rd-cloudSection__title" id="rd-plugin-catalog-others">
              {t('pluginInstall.official.others')}
            </h3>
          ) : null}
          <ul className="rd-pluginCatalog__list">{others.map((entry) => row(entry, false))}</ul>
        </section>
      ) : null}
    </div>
  );
}

/**
 * Uma linha do catálogo: o ícone, o nome, "1.1.2 · por RPG Desk · Ficha", a descrição em até duas
 * linhas e, à direita, Instalar (verde quando serve para o sistema da mesa), "Atualizar para…" ou o
 * "Instalado" em texto.
 */
function OfficialRow({
  entry,
  state,
  likely,
  busy,
  disabled,
  onInstall,
}: {
  entry: OfficialPlugin;
  state: CatalogEntryState;
  likely: boolean;
  busy: boolean;
  disabled: boolean;
  onInstall: (entry: OfficialPlugin, state: CatalogEntryState) => void;
}) {
  const t = useT();
  const language = useLanguage((s) => s.language);
  const name = localize(entry.name, language);
  const description = localize(entry.description, language);
  return (
    <li className="rd-pluginRow">
      <PluginIcon url={entry.iconUrl} table={!entry.sheets.length} />
      <div className="rd-pluginRow__text">
        <p className="rd-pluginRow__name">{name}</p>
        <p className="rd-pluginRow__meta">{pluginMeta(t, entry)}</p>
        {description ? (
          <p className="rd-pluginRow__desc" title={description}>
            {description}
          </p>
        ) : null}
      </div>
      <div className="rd-pluginRow__action">
        {state.kind === 'installed' ? (
          <span className="rd-pluginRow__done">
            <Icon name="check" size={16} />
            {t('pluginInstall.installed')}
          </span>
        ) : (
          <Button
            variant={likely && state.kind === 'install' ? 'action' : 'secondary'}
            busy={busy}
            disabled={disabled && !busy}
            aria-label={state.kind === 'update' ? t('pluginInstall.updateLabel', { name, version: entry.version }) : t('pluginInstall.installLabel', { name })}
            onClick={() => onInstall(entry, state)}
          >
            {state.kind === 'update' ? t('pluginInstall.updateTo', { version: entry.version }) : t('webInstall.install')}
          </Button>
        )}
      </div>
    </li>
  );
}
